import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  X,
  Users,
  Globe,
  Lock,
  Send,
  Heart,
  MessageCircle,
  Clock,
  Loader2,
  UserPlus,
  LogOut,
  Sparkles,
  HelpCircle,
  PartyPopper,
  Palette,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button, Card, Modal, Textarea, Input, Select, EmptyState } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { useAuth } from '../../context';
import {
  circlesService,
  CircleDto,
  CircleDetailDto,
  CirclePostDto,
  CircleCommentDto,
  CreateCircleRequest,
  CreateCirclePostRequest,
} from '../../services';
import './Circles.css';

// ==================== Constants ====================

type TabView = 'my-circles' | 'discover';
type PostType = 'Update' | 'WIP' | 'Question' | 'Celebration';

const disciplines = [
  { value: '', label: 'All Disciplines' },
  { value: 'Music', label: 'Music' },
  { value: 'Visual Arts', label: 'Visual Arts' },
  { value: 'Writing', label: 'Writing' },
  { value: 'Film', label: 'Film' },
  { value: 'Photography', label: 'Photography' },
  { value: 'Dance', label: 'Dance' },
  { value: 'Theater', label: 'Theater' },
  { value: 'Design', label: 'Design' },
  { value: 'Mixed Media', label: 'Mixed Media' },
  { value: 'Other', label: 'Other' },
];

const disciplineOptions = disciplines.filter(d => d.value !== '');

const privacyOptions = [
  { value: 'Public', label: 'Public - Anyone can discover and join' },
  { value: 'Private', label: 'Private - Invite only' },
];

const postTypeConfig: Record<PostType, { icon: React.ReactNode; label: string; color: string }> = {
  Update: { icon: <Sparkles size={14} />, label: 'Update', color: 'blue' },
  WIP: { icon: <Palette size={14} />, label: 'Work in Progress', color: 'orange' },
  Question: { icon: <HelpCircle size={14} />, label: 'Question', color: 'purple' },
  Celebration: { icon: <PartyPopper size={14} />, label: 'Celebration', color: 'green' },
};

// ==================== Helpers ====================

const formatRelativeTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// ==================== Main Component ====================

export const Circles: React.FC = () => {
  const { user } = useAuth();
  const { id: circleIdParam } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabView>('my-circles');

  // List state
  const [myCircles, setMyCircles] = useState<CircleDto[]>([]);
  const [discoverCircles, setDiscoverCircles] = useState<CircleDto[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detail state
  const [selectedCircle, setSelectedCircle] = useState<CircleDetailDto | null>(null);
  const [circlePosts, setCirclePosts] = useState<CirclePostDto[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);

  // Create circle modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateCircleRequest>({
    name: '',
    description: '',
    discipline: '',
    privacy: 'Public',
    maxMembers: 20,
  });
  const [isCreating, setIsCreating] = useState(false);

  // Create post state
  const [showPostForm, setShowPostForm] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [postType, setPostType] = useState<PostType>('Update');
  const [postMediaUrl, setPostMediaUrl] = useState('');
  const [isPosting, setIsPosting] = useState(false);

  // Comment state
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [commentTexts, setCommentTexts] = useState<Record<string, string>>({});
  const [isCommenting, setIsCommenting] = useState<string | null>(null);

  // Join/leave state
  const [isJoiningLeaving, setIsJoiningLeaving] = useState(false);

  // ==================== Data Fetching ====================

  const fetchMyCircles = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await circlesService.getUserCircles();
      setMyCircles(data);
    } catch (err) {
      console.error('Failed to fetch circles:', err);
      setError('Failed to load your circles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchDiscoverCircles = useCallback(async (discipline?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await circlesService.discoverCircles(discipline || undefined);
      setDiscoverCircles(data);
    } catch (err) {
      console.error('Failed to fetch discover circles:', err);
      setError('Failed to load circles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchCircleDetail = useCallback(async (id: string) => {
    try {
      setIsLoadingDetail(true);
      const detail = await circlesService.getCircle(id);
      if (detail) {
        setSelectedCircle(detail);
      } else {
        setError('Circle not found.');
      }
    } catch (err) {
      console.error('Failed to fetch circle detail:', err);
      setError('Failed to load circle details.');
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  const fetchCirclePosts = useCallback(async (circleId: string) => {
    try {
      setIsLoadingPosts(true);
      const posts = await circlesService.getFeed(circleId);
      setCirclePosts(posts);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setIsLoadingPosts(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    if (circleIdParam) {
      fetchCircleDetail(circleIdParam);
      fetchCirclePosts(circleIdParam);
    } else {
      fetchMyCircles();
    }
  }, [circleIdParam, fetchMyCircles, fetchCircleDetail, fetchCirclePosts]);

  // Refresh discover when tab changes or filter changes
  useEffect(() => {
    if (activeTab === 'discover' && !circleIdParam) {
      fetchDiscoverCircles(filterDiscipline);
    }
  }, [activeTab, filterDiscipline, circleIdParam, fetchDiscoverCircles]);

  // ==================== Circle Actions ====================

  const handleCreateCircle = async () => {
    if (!createForm.name.trim() || !createForm.discipline) return;

    setIsCreating(true);
    try {
      const newCircle = await circlesService.createCircle(createForm);
      setMyCircles(prev => [newCircle, ...prev]);
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '', discipline: '', privacy: 'Public', maxMembers: 20 });
      navigate(`/circles/${newCircle.id}`);
    } catch (err) {
      console.error('Failed to create circle:', err);
      setError('Failed to create circle. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinCircle = async (circleId: string) => {
    setIsJoiningLeaving(true);
    try {
      await circlesService.joinCircle(circleId);
      // Refresh the circle detail
      await fetchCircleDetail(circleId);
      await fetchCirclePosts(circleId);
      // Update my circles list
      fetchMyCircles();
    } catch (err) {
      console.error('Failed to join circle:', err);
      setError('Failed to join circle. Please try again.');
    } finally {
      setIsJoiningLeaving(false);
    }
  };

  const handleLeaveCircle = async (circleId: string) => {
    setIsJoiningLeaving(true);
    try {
      await circlesService.leaveCircle(circleId);
      setSelectedCircle(null);
      navigate('/circles');
      fetchMyCircles();
    } catch (err) {
      console.error('Failed to leave circle:', err);
      setError('Failed to leave circle. Please try again.');
    } finally {
      setIsJoiningLeaving(false);
    }
  };

  const handleOpenCircle = (circleId: string) => {
    navigate(`/circles/${circleId}`);
  };

  const handleBackToList = () => {
    setSelectedCircle(null);
    setCirclePosts([]);
    navigate('/circles');
  };

  // ==================== Post Actions ====================

  const handleCreatePost = async () => {
    if (!postContent.trim() || !selectedCircle) return;

    setIsPosting(true);
    try {
      const request: CreateCirclePostRequest = {
        content: postContent.trim(),
        postType: postType,
        mediaUrls: postMediaUrl.trim() ? [postMediaUrl.trim()] : undefined,
      };
      const newPost = await circlesService.createPost(selectedCircle.id, request);
      setCirclePosts(prev => [newPost, ...prev]);
      setPostContent('');
      setPostMediaUrl('');
      setPostType('Update');
      setShowPostForm(false);
    } catch (err) {
      console.error('Failed to create post:', err);
      setError('Failed to create post. Please try again.');
    } finally {
      setIsPosting(false);
    }
  };

  const handleLikePost = async (postId: string) => {
    if (!selectedCircle) return;

    try {
      const result = await circlesService.likePost(selectedCircle.id, postId);
      setCirclePosts(prev =>
        prev.map(post =>
          post.id === postId
            ? { ...post, likeCount: result.liked ? post.likeCount + 1 : Math.max(0, post.likeCount - 1) }
            : post
        )
      );
    } catch (err) {
      console.error('Failed to like post:', err);
    }
  };

  const handleCreateComment = async (postId: string) => {
    const content = commentTexts[postId]?.trim();
    if (!content || !selectedCircle) return;

    setIsCommenting(postId);
    try {
      const newComment = await circlesService.createComment(selectedCircle.id, postId, { content });
      setCirclePosts(prev =>
        prev.map(post =>
          post.id === postId
            ? {
                ...post,
                comments: [...(post.comments || []), newComment],
                commentCount: post.commentCount + 1,
              }
            : post
        )
      );
      setCommentTexts(prev => ({ ...prev, [postId]: '' }));
    } catch (err) {
      console.error('Failed to create comment:', err);
    } finally {
      setIsCommenting(null);
    }
  };

  const toggleComments = (postId: string) => {
    setExpandedComments(prev => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  // ==================== Filtering ====================

  const isMember = selectedCircle?.members?.some(m => m.userId === user?.id && m.status === 'Active');
  const isCreator = selectedCircle?.creatorId === user?.id;

  const filteredMyCircles = myCircles.filter(circle => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      circle.name.toLowerCase().includes(query) ||
      circle.description.toLowerCase().includes(query) ||
      circle.discipline.toLowerCase().includes(query)
    );
  });

  const filteredDiscoverCircles = discoverCircles.filter(circle => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      circle.name.toLowerCase().includes(query) ||
      circle.description.toLowerCase().includes(query)
    );
  });

  // ==================== Render: Circle Detail View ====================

  if (circleIdParam) {
    if (isLoadingDetail) {
      return (
        <AppLayout pageTitle="Circle" showBackButton onBack={handleBackToList}>
          <div className="circles">
            <div className="circles__loading">
              <Loader2 size={32} className="circles__loading-spinner" />
              <p>Loading circle...</p>
            </div>
          </div>
        </AppLayout>
      );
    }

    if (!selectedCircle) {
      return (
        <AppLayout pageTitle="Circle" showBackButton onBack={handleBackToList}>
          <div className="circles">
            <div className="circles__error">
              <p>Circle not found.</p>
              <Button onClick={handleBackToList}>Back to Circles</Button>
            </div>
          </div>
        </AppLayout>
      );
    }

    return (
      <AppLayout pageTitle={selectedCircle.name} showBackButton onBack={handleBackToList}>
        <div className="circles">
          <div className="circles__ambient">
            <div className="circles__ambient-shape circles__ambient-shape--1" />
            <div className="circles__ambient-shape circles__ambient-shape--2" />
          </div>

          <div className="circles__detail">
            {/* Circle Header */}
            <div className="circles__detail-header">
              <div className="circles__detail-info">
                <p className="circles__detail-description">{selectedCircle.description}</p>
                <div className="circles__detail-meta">
                  <span className="circles__detail-meta-item">
                    <Users size={14} />
                    {selectedCircle.memberCount}/{selectedCircle.maxMembers} members
                  </span>
                  <span className="circles__detail-meta-item">
                    {selectedCircle.privacy === 'Public' ? <Globe size={14} /> : <Lock size={14} />}
                    {selectedCircle.privacy}
                  </span>
                  <span className="circles__detail-meta-item circles__detail-discipline">
                    {selectedCircle.discipline}
                  </span>
                </div>
              </div>

              <div className="circles__detail-actions">
                {isMember ? (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => setShowPostForm(!showPostForm)}
                    >
                      <Plus size={16} />
                      New Post
                    </Button>
                    {!isCreator && (
                      <Button
                        variant="ghost"
                        onClick={() => handleLeaveCircle(selectedCircle.id)}
                        disabled={isJoiningLeaving}
                      >
                        <LogOut size={16} />
                        Leave
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    onClick={() => handleJoinCircle(selectedCircle.id)}
                    disabled={isJoiningLeaving || selectedCircle.memberCount >= selectedCircle.maxMembers}
                  >
                    <UserPlus size={16} />
                    {isJoiningLeaving ? 'Joining...' : 'Join Circle'}
                  </Button>
                )}
              </div>
            </div>

            {/* Post Creation Form */}
            {showPostForm && isMember && (
              <Card variant="default" padding="md" className="circles__post-form">
                <div className="circles__post-type-selector">
                  {(Object.keys(postTypeConfig) as PostType[]).map(type => (
                    <button
                      key={type}
                      className={`circles__post-type-btn circles__post-type-btn--${postTypeConfig[type].color} ${postType === type ? 'circles__post-type-btn--active' : ''}`}
                      onClick={() => setPostType(type)}
                    >
                      {postTypeConfig[type].icon}
                      <span>{postTypeConfig[type].label}</span>
                    </button>
                  ))}
                </div>
                <Textarea
                  placeholder="Share with your circle..."
                  value={postContent}
                  onChange={(e) => setPostContent(e.target.value)}
                  rows={3}
                  autoFocus
                />
                <Input
                  placeholder="Add a media URL (optional)"
                  value={postMediaUrl}
                  onChange={(e) => setPostMediaUrl(e.target.value)}
                />
                <div className="circles__post-form-actions">
                  <Button variant="ghost" onClick={() => setShowPostForm(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreatePost}
                    disabled={isPosting || !postContent.trim()}
                  >
                    {isPosting ? 'Posting...' : 'Post'}
                  </Button>
                </div>
              </Card>
            )}

            {/* Members Panel */}
            <div className="circles__members-panel">
              <h3 className="circles__section-title">
                <Users size={16} />
                Members ({selectedCircle.members?.length || 0})
              </h3>
              <div className="circles__members-list">
                {selectedCircle.members?.map(member => (
                  <div key={member.id} className="circles__member">
                    <div className="circles__member-avatar">
                      {member.userId.charAt(0).toUpperCase()}
                    </div>
                    <div className="circles__member-info">
                      <span className="circles__member-role">{member.role}</span>
                      <span className="circles__member-joined">
                        Joined {formatRelativeTime(member.joinedAt)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Feed */}
            <div className="circles__feed">
              <h3 className="circles__section-title">Feed</h3>
              {isLoadingPosts ? (
                <div className="circles__loading">
                  <Loader2 size={24} className="circles__loading-spinner" />
                  <p>Loading posts...</p>
                </div>
              ) : circlePosts.length > 0 ? (
                circlePosts.map(post => (
                  <Card key={post.id} variant="default" padding="md" className="circles__post-card">
                    {/* Post Header */}
                    <div className="circles__post-header">
                      <span className={`circles__post-type circles__post-type--${postTypeConfig[post.postType as PostType]?.color || 'blue'}`}>
                        {postTypeConfig[post.postType as PostType]?.icon}
                        {postTypeConfig[post.postType as PostType]?.label || post.postType}
                      </span>
                      <span className="circles__post-time">
                        <Clock size={12} />
                        {formatRelativeTime(post.createdAt)}
                      </span>
                    </div>

                    {/* Post Content */}
                    <p className="circles__post-content">{post.content}</p>

                    {/* Media */}
                    {post.mediaUrls && post.mediaUrls.length > 0 && (
                      <div className="circles__post-media">
                        {post.mediaUrls.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="circles__post-media-link">
                            {url}
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Post Actions */}
                    <div className="circles__post-actions">
                      <button
                        className="circles__post-action-btn"
                        onClick={() => handleLikePost(post.id)}
                      >
                        <Heart size={16} />
                        <span>{post.likeCount}</span>
                      </button>
                      <button
                        className="circles__post-action-btn"
                        onClick={() => toggleComments(post.id)}
                      >
                        <MessageCircle size={16} />
                        <span>{post.commentCount}</span>
                        {expandedComments.has(post.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </div>

                    {/* Comments Section */}
                    {expandedComments.has(post.id) && (
                      <div className="circles__comments">
                        {post.comments && post.comments.length > 0 && (
                          <div className="circles__comments-list">
                            {post.comments.map((comment: CircleCommentDto) => (
                              <div key={comment.id} className="circles__comment">
                                <div className="circles__comment-avatar">
                                  {comment.authorId.charAt(0).toUpperCase()}
                                </div>
                                <div className="circles__comment-body">
                                  <p className="circles__comment-content">{comment.content}</p>
                                  <span className="circles__comment-time">
                                    {formatRelativeTime(comment.createdAt)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {isMember && (
                          <div className="circles__comment-form">
                            <input
                              type="text"
                              placeholder="Write a comment..."
                              value={commentTexts[post.id] || ''}
                              onChange={(e) =>
                                setCommentTexts(prev => ({ ...prev, [post.id]: e.target.value }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleCreateComment(post.id);
                                }
                              }}
                            />
                            <button
                              className="circles__comment-send"
                              onClick={() => handleCreateComment(post.id)}
                              disabled={isCommenting === post.id || !commentTexts[post.id]?.trim()}
                            >
                              <Send size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                ))
              ) : (
                <div className="circles__feed-empty">
                  <MessageCircle size={32} />
                  <p>No posts yet. Be the first to share!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ==================== Render: List View (My Circles / Discover) ====================

  return (
    <AppLayout pageTitle="Creative Circles">
      <div className="circles">
        <div className="circles__ambient">
          <div className="circles__ambient-shape circles__ambient-shape--1" />
          <div className="circles__ambient-shape circles__ambient-shape--2" />
        </div>

        <main className="circles__main">
          {/* Intro */}
          <div className="circles__intro">
            <p>Connect with fellow artists. Share work. Grow together.</p>
          </div>

          {/* Tabs */}
          <div className="circles__tabs">
            <button
              className={`circles__tab ${activeTab === 'my-circles' ? 'circles__tab--active' : ''}`}
              onClick={() => setActiveTab('my-circles')}
            >
              <Users size={16} />
              My Circles
            </button>
            <button
              className={`circles__tab ${activeTab === 'discover' ? 'circles__tab--active' : ''}`}
              onClick={() => setActiveTab('discover')}
            >
              <Globe size={16} />
              Discover
            </button>
          </div>

          {/* Search & Filters */}
          <div className="circles__filters">
            <div className="circles__search">
              <Search size={18} />
              <input
                type="text"
                placeholder={activeTab === 'my-circles' ? 'Search your circles...' : 'Search circles...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')}>
                  <X size={16} />
                </button>
              )}
            </div>

            {activeTab === 'discover' && (
              <div className="circles__discipline-filter">
                <Select
                  options={disciplines}
                  value={filterDiscipline}
                  onChange={(e) => setFilterDiscipline(e.target.value)}
                  placeholder="Filter by discipline"
                />
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="circles__error-banner">
              <p>{error}</p>
              <button onClick={() => setError(null)}>
                <X size={16} />
              </button>
            </div>
          )}

          {/* Circle Grid */}
          <div className="circles__grid">
            {isLoading ? (
              <div className="circles__loading">
                <Loader2 size={32} className="circles__loading-spinner" />
                <p>Loading circles...</p>
              </div>
            ) : activeTab === 'my-circles' ? (
              filteredMyCircles.length > 0 ? (
                filteredMyCircles.map((circle, index) => (
                  <Card
                    key={circle.id}
                    variant="default"
                    padding="md"
                    className="circles__circle-card"
                    onClick={() => handleOpenCircle(circle.id)}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="circles__circle-card-header">
                      <h3 className="circles__circle-name">{circle.name}</h3>
                      <span className="circles__circle-privacy">
                        {circle.privacy === 'Public' ? <Globe size={14} /> : <Lock size={14} />}
                      </span>
                    </div>
                    <p className="circles__circle-desc">{circle.description}</p>
                    <div className="circles__circle-footer">
                      <span className="circles__circle-discipline">{circle.discipline}</span>
                      <span className="circles__circle-members">
                        <Users size={14} />
                        {circle.memberCount}/{circle.maxMembers}
                      </span>
                    </div>
                    {circle.lastActivityAt && (
                      <span className="circles__circle-activity">
                        <Clock size={12} />
                        Active {formatRelativeTime(circle.lastActivityAt)}
                      </span>
                    )}
                  </Card>
                ))
              ) : (
                <EmptyState
                  title="No circles yet"
                  message="Create or join a circle to connect with other artists and share your creative journey."
                  icon={<Users size={48} />}
                  action={{
                    label: 'Create a Circle',
                    onClick: () => setShowCreateModal(true),
                  }}
                />
              )
            ) : filteredDiscoverCircles.length > 0 ? (
              filteredDiscoverCircles.map((circle, index) => (
                <Card
                  key={circle.id}
                  variant="default"
                  padding="md"
                  className="circles__circle-card"
                  onClick={() => handleOpenCircle(circle.id)}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="circles__circle-card-header">
                    <h3 className="circles__circle-name">{circle.name}</h3>
                    <span className="circles__circle-privacy">
                      {circle.privacy === 'Public' ? <Globe size={14} /> : <Lock size={14} />}
                    </span>
                  </div>
                  <p className="circles__circle-desc">{circle.description}</p>
                  <div className="circles__circle-footer">
                    <span className="circles__circle-discipline">{circle.discipline}</span>
                    <span className="circles__circle-members">
                      <Users size={14} />
                      {circle.memberCount}/{circle.maxMembers}
                    </span>
                  </div>
                </Card>
              ))
            ) : (
              <EmptyState
                title="No circles found"
                message={filterDiscipline ? `No public circles for "${filterDiscipline}" yet. Be the first to create one!` : 'No public circles available yet. Create the first one!'}
                icon={<Search size={48} />}
                action={{
                  label: 'Create a Circle',
                  onClick: () => setShowCreateModal(true),
                }}
              />
            )}
          </div>
        </main>

        {/* FAB - Create Circle */}
        <button
          className="circles__fab"
          onClick={() => setShowCreateModal(true)}
          aria-label="Create circle"
        >
          <Plus size={28} />
        </button>

        {/* Create Circle Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create a Circle"
          size="md"
        >
          <div className="circles__create-form">
            <Input
              placeholder="Circle name"
              value={createForm.name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
              autoFocus
            />
            <Textarea
              placeholder="What is this circle about?"
              value={createForm.description}
              onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
            <Select
              label="Discipline"
              options={disciplineOptions}
              value={createForm.discipline}
              onChange={(e) => setCreateForm(prev => ({ ...prev, discipline: e.target.value }))}
              placeholder="Select a discipline"
            />
            <Select
              label="Privacy"
              options={privacyOptions}
              value={createForm.privacy}
              onChange={(e) => setCreateForm(prev => ({ ...prev, privacy: e.target.value }))}
            />
            <Input
              type="number"
              placeholder="Max members (default: 20)"
              value={createForm.maxMembers?.toString() || ''}
              onChange={(e) => setCreateForm(prev => ({ ...prev, maxMembers: parseInt(e.target.value) || 20 }))}
              min={2}
              max={100}
            />
            <div className="circles__create-form-actions">
              <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateCircle}
                disabled={isCreating || !createForm.name.trim() || !createForm.discipline}
              >
                {isCreating ? 'Creating...' : 'Create Circle'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AppLayout>
  );
};

export default Circles;
